import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  Chip,
  IconButton,
  Card,
  CardContent,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Skeleton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar
} from '@mui/material';
import {
  Star,
  Article,
  QuestionAnswer,
  PhotoCamera,
  Refresh
} from '@mui/icons-material';
import api from '../services/api';

const LazyDataViewer = ({ snapshotId, dataType, title, icon: Icon }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [summary, setSummary] = useState(null);

  const loadSummary = useCallback(async () => {
    try {
      console.log(`🔍 Loading summary for snapshot ${snapshotId}`);
      const response = await api.get(`/api/snapshots/${snapshotId}/summary`);
      console.log(`📊 Summary response:`, response.data);
      setSummary(response.data);
    } catch (error) {
      console.error(`❌ Error loading summary for snapshot ${snapshotId}:`, error);
      console.error(`   Status: ${error.response?.status}, Message: ${error.response?.data?.error || error.message}`);
    }
  }, [snapshotId]);

  // Load summary data on mount
  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const loadData = useCallback(async (pageNum = 1, size = pageSize) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`🔍 Loading ${dataType} data for snapshot ${snapshotId}, page ${pageNum}, size ${size}`);
      
      const response = await api.get(`/api/snapshots/${snapshotId}`, {
        params: {
          dataType,
          page: pageNum,
          limit: size
        }
      });

      console.log(`📊 Response for ${dataType}:`, response.data);
      
      if (response.data.data) {
        setData(response.data.data.items || []);
        setTotalPages(response.data.data.totalPages || 1);
        setTotalItems(response.data.data.total || 0);
        setPage(pageNum);
      } else {
        console.warn(`⚠️ No data structure found for ${dataType}`);
        setData([]);
        setTotalPages(1);
        setTotalItems(0);
      }
    } catch (error) {
      console.error(`❌ Error loading ${dataType} data:`, error);
      setError(`Failed to load ${dataType} data: ${error.response?.data?.error || error.message}`);
      setData([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [snapshotId, dataType, pageSize]);

  useEffect(() => {
    if (snapshotId) {
      loadData();
    }
  }, [snapshotId, loadData]);

  const handlePageChange = (event, newPage) => {
    loadData(newPage);
  };

  const handlePageSizeChange = (event) => {
    const newSize = event.target.value;
    setPageSize(newSize);
    loadData(1, newSize);
  };

  const renderReviewItem = (review) => (
    <ListItem key={review.name} divider>
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'primary.main' }}>
          <Star />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle2">
              {review.reviewer?.displayName || 'Anonymous'}
            </Typography>
            <Box display="flex" alignItems="center">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  sx={{
                    fontSize: 16,
                    color: i < review.starRating ? 'warning.main' : 'grey.300'
                  }}
                />
              ))}
            </Box>
            <Chip
              label={review.starRating} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
          </Box>
        }
        secondary={
          <Box>
            <Typography variant="body2" color="textSecondary" component="span">
              {new Date(review.createTime).toLocaleDateString()}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }} component="div">
              {review.comment}
            </Typography>
          </Box>
        }
      />
    </ListItem>
  );

        const renderPostItem = (post) => (
        <ListItem key={post.name} divider>
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              <Article />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2">
                  {post.summary}
                </Typography>
                <Chip
                  label={post.topicType} 
                  size="small" 
                  color="secondary" 
                  variant="outlined"
                />
              </Box>
            }
            secondary={
              <Box>
                <Typography variant="body2" color="textSecondary" component="span">
                  {new Date(post.createTime).toLocaleDateString()}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }} component="div">
                  {post.callToAction?.actionType && (
                    <Chip
                      label={`Call to Action: ${post.callToAction.actionType}`}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ mr: 1 }}
                    />
                  )}
                </Typography>
              </Box>
            }
          />
        </ListItem>
      );

        const renderQAItem = (question) => (
        <ListItem key={question.name} divider>
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: 'info.main' }}>
              <QuestionAnswer />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2">
                  {question.text}
                </Typography>
                <Chip
                  label={question.author?.displayName || 'Anonymous'} 
                  size="small" 
                  color="info" 
                  variant="outlined"
                />
              </Box>
            }
            secondary={
              <Box>
                <Typography variant="body2" color="textSecondary" component="span">
                  {new Date(question.createTime).toLocaleDateString()}
                </Typography>
                {question.topAnswers?.length > 0 && (
                  <Typography variant="body2" sx={{ mt: 1 }} component="div">
                    <strong>Top Answer:</strong> {question.topAnswers[0].text}
                  </Typography>
                )}
              </Box>
            }
          />
        </ListItem>
      );

        const renderPhotoItem = (photo) => (
        <ListItem key={photo.name} divider>
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: 'success.main' }}>
              <PhotoCamera />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2">
                  Photo by {photo.attribution?.profileName || 'Unknown'}
                </Typography>
                <Chip
                  label={photo.mediaFormat} 
                  size="small" 
                  color="success" 
                  variant="outlined"
                />
              </Box>
            }
            secondary={
              <Box>
                <Typography variant="body2" color="textSecondary" component="span">
                  {new Date(photo.createTime).toLocaleDateString()}
                </Typography>
                {photo.insights && (
                  <Typography variant="body2" sx={{ mt: 1 }} component="div">
                    <strong>Views:</strong> {photo.insights.viewCount || 0}
                  </Typography>
                )}
              </Box>
            }
          />
        </ListItem>
      );

  const renderItem = (item) => {
    switch (dataType) {
      case 'reviews':
        return renderReviewItem(item);
      case 'posts':
        return renderPostItem(item);
      case 'qa':
        return renderQAItem(item);
      case 'photos':
        return renderPhotoItem(item);
      default:
        return (
          <ListItem key={item.name || item.id} divider>
            <ListItemText
              primary={JSON.stringify(item)}
              secondary="Unknown data type"
            />
          </ListItem>
        );
    }
  };

  const getItemCount = () => {
    if (!summary?.counts) return 0;
    switch (dataType) {
      case 'reviews': return summary.counts.reviews;
      case 'posts': return summary.counts.posts;
      case 'qa': return summary.counts.qa;
      case 'photos': return summary.counts.photos;
      default: return 0;
    }
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Icon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box flex={1}>
            <Typography variant="h6">{title}</Typography>
            <Typography variant="body2" color="textSecondary">
              {getItemCount()} total items
            </Typography>
          </Box>
          <IconButton onClick={() => loadData()} disabled={loading}>
            <Refresh />
          </IconButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Controls */}
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Page Size</InputLabel>
            <Select
              value={pageSize}
              label="Page Size"
              onChange={handlePageSizeChange}
            >
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
          
          <Typography variant="body2" color="textSecondary">
            Showing {data.length} of {totalItems} items
          </Typography>
        </Box>

        {/* Data List */}
        {loading ? (
          <Box>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={80} sx={{ mb: 1 }} />
            ))}
          </Box>
        ) : (
          <List>
            {data.map(renderItem)}
          </List>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        {data.length === 0 && !loading && (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="textSecondary">
              No {dataType} data available
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default LazyDataViewer; 